import axios from '../axiosInstance.js';

const REPORT_URL = '/api/errors';
const DEBOUNCE_MS = 2000;
const MAX_QUEUE = 20;

const recentKeys = new Set();
let queue = [];
let flushTimer = null;

function fingerprint(message, stack) {
  return `${message}::${(stack || '').slice(0, 120)}`;
}

function getUserId() {
  try {
    const raw = sessionStorage.getItem('sessionData');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user_id || parsed?.id || null;
  } catch {
    return null;
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, DEBOUNCE_MS);
}

async function flush() {
  clearTimeout(flushTimer);
  flushTimer = null;
  const batch = queue.splice(0, MAX_QUEUE);
  for (const entry of batch) {
    try {
      await axios.post(REPORT_URL, entry);
    } catch {
      // Silently drop — we can't report errors about error reporting
    }
  }
  if (queue.length > 0) scheduleFlush();
}

function report({ message, stack, component, level, metadata }) {
  const key = fingerprint(message, stack);
  if (recentKeys.has(key)) return;

  recentKeys.add(key);
  setTimeout(() => recentKeys.delete(key), 10_000);

  queue.push({
    source: 'frontend',
    level: level || 'error',
    message: message || 'Unknown error',
    stack: stack || null,
    component: component || null,
    url: window.location.href,
    user_id: getUserId(),
    metadata: {
      userAgent: navigator.userAgent,
      ...metadata,
    },
  });

  if (queue.length >= MAX_QUEUE) {
    flush();
  } else {
    scheduleFlush();
  }
}

let handlersReady = false;

function setupGlobalHandlers() {
  if (handlersReady) return;
  handlersReady = true;

  // user may reload right after a crash — don't lose what's still queued
  window.addEventListener('pagehide', () => {
    if (!navigator.sendBeacon) return;
    for (const entry of queue.splice(0)) {
      navigator.sendBeacon(REPORT_URL, new Blob([JSON.stringify(entry)], { type: 'application/json' }));
    }
  });

  window.addEventListener('error', (event) => {
    report({
      message: event.message || String(event.error),
      stack: event.error?.stack || null,
      component: null,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason;
    report({
      message: err?.message || String(err),
      stack: err?.stack || null,
      component: null,
      level: 'error',
      metadata: { type: 'unhandledrejection' },
    });
  });
}

const errorReporter = { report, setupGlobalHandlers, flush };

export default errorReporter;
